import { useEffect, useState } from "react";
import { CalendarCheck, Loader2, Check, Ruler, Palette } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";

const Pill = ({ on, onClick, children, testid, disabled }) => (
  <button type="button" onClick={onClick} data-testid={testid} disabled={disabled} aria-disabled={disabled}
    className={`rounded-full border px-4 py-2 text-sm transition-colors ${disabled ? "border-white/10 text-brand-muted-light line-through cursor-not-allowed" : on ? "bg-brand-ink text-brand-cream border-brand-ink" : "border-brand-line hover:border-brand-accent text-brand-ink"}`}>
    {children}
  </button>
);

export default function BookVisit({ q, open, onDone }) {
  const [opts, setOpts] = useState(null);
  const [existing, setExisting] = useState([]);
  const [show, setShow] = useState(open);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({ visit_type: "Site Measurement", date: "", time_window: "Morning (9–12)", name: q.client.name, phone: q.client.phone || "", address: q.client.city || "", note: "" });

  useEffect(() => {
    api.get("/bookings/options").then((r) => { setOpts(r.data); setF((x) => ({ ...x, date: x.date || r.data.min_date })); });
    api.get(`/quotes/${q.id}/bookings`).then((r) => setExisting(r.data)).catch(() => {});
  }, [q.id]);
  useEffect(() => { if (open) setShow(true); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { ...f, quote_id: q.id });
      setDone(data);
      toast.success("Site visit requested — we'll confirm shortly");
      onDone?.(data);
    } catch (err) { toast.error(formatErr(err)); api.get("/bookings/options").then((r) => setOpts(r.data)).catch(() => {}); }
    setBusy(false);
  };

  const blocked = opts?.blocked_dates?.includes(f.date);
  const fullWins = opts?.full_slots?.[f.date] || [];
  const dayFull = opts && fullWins.length >= (opts.time_windows?.length || 3);
  const dateBad = blocked || dayFull;
  const last = done || existing[0];
  return (
    <div id="book-visit" className="rounded-3xl bg-brand-ink-soft text-brand-cream p-7 md:p-9 grain relative overflow-hidden" data-testid="book-visit-card">
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.3em] text-brand-accent inline-flex items-center gap-2"><CalendarCheck className="w-3.5 h-3.5" /> Next step</p>
        {last ? (
          <div data-testid="booking-confirmation">
            <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">Your visit is requested.</h3>
            <p className="text-sm text-brand-line mt-3 leading-relaxed">
              <Check className="inline w-4 h-4 text-brand-accent mr-1" />{last.visit_type} · {new Date(last.date + "T00:00:00").toLocaleDateString("en-IN", { dateStyle: "long" })} · {last.time_window}
            </p>
            <p className="text-sm text-brand-muted-light mt-3">homiin will confirm this slot by phone or email. A confirmation has been sent to {q.client.email}.</p>
            <span className="inline-block mt-5 text-[10px] uppercase tracking-[0.2em] bg-brand-accent/20 text-brand-accent px-3 py-1 rounded-full" data-testid="booking-status">{last.status}</span>
          </div>
        ) : !show ? (
          <>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">Book a free site visit.</h3>
            <p className="text-sm text-brand-line mt-3 leading-relaxed">Our designer measures your home, validates this estimate and walks you through material samples — at no cost.</p>
            <button onClick={() => setShow(true)} className="mt-6 rounded-full bg-brand-accent hover:bg-brand-accent-dark text-white px-6 py-3 text-sm transition-colors" data-testid="book-visit-open-btn">Book Free Site Visit</button>
          </>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-5" data-testid="book-visit-form">
            <div className="flex flex-wrap gap-2">
              {(opts?.visit_types || []).map((v) => (
                <Pill key={v} on={f.visit_type === v} onClick={() => setF({ ...f, visit_type: v })} testid={`visit-type-${v.split(" ")[0].toLowerCase()}`}>
                  {v === "Site Measurement" ? <Ruler className="inline w-3.5 h-3.5 mr-1.5" /> : <Palette className="inline w-3.5 h-3.5 mr-1.5" />}{v}
                </Pill>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-brand-line text-xs">Preferred date</Label>
                <Input type="date" required min={opts?.min_date} max={opts?.max_date} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-11 bg-white/5 border-white/15 text-brand-cream [color-scheme:dark]" data-testid="booking-date-input" />
                {dateBad && <p className="text-xs text-brand-accent" data-testid="booking-date-unavailable">{blocked ? "homiin isn't visiting on this date — please pick another day." : "All windows on this date are booked — please pick another day."}</p>}</div>
              <div className="space-y-1.5"><Label className="text-brand-line text-xs">Time window</Label>
                <div className="flex flex-wrap gap-2 pt-1">{(opts?.time_windows || []).map((w) => {
                  const full = fullWins.includes(w);
                  return <Pill key={w} on={f.time_window === w && !full} onClick={() => !full && setF({ ...f, time_window: w })} testid={`time-window-${w.split(" ")[0].toLowerCase()}`} disabled={full}>{w}{full && <span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-70">Full</span>}</Pill>;
                })}</div></div>
              <div className="space-y-1.5"><Label className="text-brand-line text-xs">Your name</Label>
                <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 bg-white/5 border-white/15 text-brand-cream" data-testid="booking-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-brand-line text-xs">Phone</Label>
                <Input required type="tel" minLength={7} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 98xxx xxxxx" className="h-11 bg-white/5 border-white/15 text-brand-cream" data-testid="booking-phone-input" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-brand-line text-xs">Site address</Label>
                <Input required minLength={5} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Flat, building, area, city" className="h-11 bg-white/5 border-white/15 text-brand-cream" data-testid="booking-address-input" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-brand-line text-xs">Note (optional)</Label>
                <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-brand-cream" data-testid="booking-note-input" /></div>
            </div>
            <p className="text-xs text-brand-muted-light">Confirmation goes to {q.client.email}.</p>
            <div className="flex gap-3">
              <button disabled={busy || dateBad || fullWins.includes(f.time_window)} className="rounded-full bg-brand-accent hover:bg-brand-accent-dark text-white px-6 py-3 text-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60" data-testid="booking-submit-btn">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Request visit
              </button>
              <button type="button" onClick={() => setShow(false)} className="text-sm text-brand-line hover:text-white px-4" data-testid="booking-cancel-btn">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
